// app/javascript/components/payroll/PersonnelSelect.jsx
import React, { useState, useMemo } from 'react';
import { toSomaliDept } from '../../utils/deptTranslations';

const PersonnelSelect = ({
  personnelList = [],
  personnelStructures = [],
  value,
  onChange,
  required = false,
  label = "Select Personnel Member",
  placeholder = "-- Select Personnel Member --",
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRank, setSelectedRank] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedSubDept, setSelectedSubDept] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all', 'unassigned', 'assigned'

  // Map of active personnel salary structures
  const activeStructureMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(personnelStructures)) {
      personnelStructures.forEach(st => {
        if (st.army_personnel_id) {
          const key = String(st.army_personnel_id);
          if (st.status === 'active' || !map.has(key)) {
            map.set(key, st);
          }
        }
      });
    }
    return map;
  }, [personnelStructures]);

  // Extract unique ranks for quick filtering
  const ranks = useMemo(() => {
    const rSet = new Set();
    personnelList.forEach(p => { if (p.rank) rSet.add(p.rank); });
    return Array.from(rSet).sort();
  }, [personnelList]);

  // Extract unique departments (in Somali)
  const departments = useMemo(() => {
    const dMap = new Map();
    personnelList.forEach(p => {
      const name = p.department?.name || p.department_name;
      if (name) {
        const key = String(p.department?.id || name);
        const somaliName = toSomaliDept(name);
        dMap.set(key, somaliName);
      }
    });
    return Array.from(dMap.entries());
  }, [personnelList]);

  // Extract unique sub-departments (in Somali)
  const subDepartments = useMemo(() => {
    const sdMap = new Map();
    personnelList.forEach(p => {
      const name = p.sub_department?.name || p.sub_department_name;
      if (name) {
        const key = String(p.sub_department?.id || name);
        const somaliName = toSomaliDept(name);
        sdMap.set(key, somaliName);
      }
    });
    return Array.from(sdMap.entries());
  }, [personnelList]);

  // Extract unique organizations / forces
  const organizations = useMemo(() => {
    const oMap = new Map();
    personnelList.forEach(p => {
      const orgName = p.organization || p.army_type?.name || (p.army_type_id ? `Force #${p.army_type_id}` : null);
      if (orgName) {
        oMap.set(orgName, orgName);
      }
    });
    return Array.from(oMap.entries());
  }, [personnelList]);

  // Filtered personnel list based on search term, rank, dept, sub-dept, org, and assignment status
  const filteredList = useMemo(() => {
    return personnelList.filter(p => {
      const isAssigned = activeStructureMap.has(String(p.id));

      if (assignmentFilter === 'unassigned' && isAssigned) return false;
      if (assignmentFilter === 'assigned' && !isAssigned) return false;

      const fullName = (p.full_name || `${p.first_name || ''} ${p.last_name || ''}`).toLowerCase();
      const armyId = (p.army_id || '').toLowerCase();
      const phone = (p.phone || p.phone_number || '').toLowerCase();
      const rankStr = (p.rank || '').toLowerCase();

      const rawDeptName = p.department?.name || p.department_name || '';
      const deptName = rawDeptName.toLowerCase();
      const somaliDeptName = toSomaliDept(rawDeptName).toLowerCase();

      const rawSubDeptName = p.sub_department?.name || p.sub_department_name || '';
      const subDeptName = rawSubDeptName.toLowerCase();
      const somaliSubDeptName = toSomaliDept(rawSubDeptName).toLowerCase();

      const orgName = (p.organization || p.army_type?.name || '').toLowerCase();

      // 1. Unified Search Query (matches Army ID, Name, Phone, Rank, Dept, SubDept, Org)
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase().trim();
        const matchesQuery = armyId.includes(query) ||
                             fullName.includes(query) ||
                             phone.includes(query) ||
                             rankStr.includes(query) ||
                             deptName.includes(query) ||
                             somaliDeptName.includes(query) ||
                             subDeptName.includes(query) ||
                             somaliSubDeptName.includes(query) ||
                             orgName.includes(query);
        if (!matchesQuery) return false;
      }

      // 2. Rank filter match
      if (selectedRank && p.rank !== selectedRank) {
        return false;
      }

      // 3. Department filter match
      if (selectedDept) {
        const pDeptId = p.department ? String(p.department.id) : String(p.department_id || '');
        const pSomali = toSomaliDept(rawDeptName);
        if (pDeptId !== selectedDept && rawDeptName !== selectedDept && pSomali !== selectedDept) {
          return false;
        }
      }

      // 4. Sub-Department filter match
      if (selectedSubDept) {
        const pSubDeptId = p.sub_department ? String(p.sub_department.id) : String(p.sub_department_id || '');
        const pSomali = toSomaliDept(rawSubDeptName);
        if (pSubDeptId !== selectedSubDept && rawSubDeptName !== selectedSubDept && pSomali !== selectedSubDept) {
          return false;
        }
      }

      // 5. Organization / Force filter match
      if (selectedOrg) {
        const pOrg = p.organization || p.army_type?.name || '';
        if (pOrg !== selectedOrg) {
          return false;
        }
      }

      return true;
    });
  }, [personnelList, activeStructureMap, assignmentFilter, searchTerm, selectedRank, selectedDept, selectedSubDept, selectedOrg]);

  const handleSelect = (e) => {
    const selectedId = e.target.value;
    const selectedObj = personnelList.find(p => String(p.id) === String(selectedId));
    onChange(selectedId, selectedObj);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRank('');
    setSelectedDept('');
    setSelectedSubDept('');
    setSelectedOrg('');
  };

  const activeFilterCount = [searchTerm, selectedRank, selectedDept, selectedSubDept, selectedOrg].filter(Boolean).length;

  return (
    <div className="personnel-select-container" style={{ width: '100%' }}>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: 0 }}>
            {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            Showing <strong>{filteredList.length}</strong> of <strong>{personnelList.length}</strong> personnel
          </span>
        </div>
      )}

      {/* Advanced Filter Toolbar */}
      <div style={{
        display: 'flex',
        gap: 6,
        marginBottom: 8,
        flexWrap: 'wrap',
        background: '#f8fafc',
        padding: '8px 10px',
        borderRadius: 6,
        border: '1px solid #cbd5e1'
      }}>
        {/* Universal Search Input */}
        <div style={{ flex: '2 1 180px', minWidth: 150 }}>
          <input
            type="text"
            placeholder="🔍 Search ID, Name, Phone, Rank, Dept, Org..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            disabled={disabled}
            style={{
              width: '100%',
              padding: '6px 9px',
              fontSize: 12,
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              outline: 'none',
              background: '#fff'
            }}
          />
        </div>

        {/* Organization / Force Filter */}
        {organizations.length > 0 && (
          <div style={{ flex: '1 1 120px', minWidth: 110 }}>
            <select
              value={selectedOrg}
              onChange={e => setSelectedOrg(e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '6px 6px',
                fontSize: 12,
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                background: '#fff'
              }}
            >
              <option value="">All Organizations / Forces</option>
              {organizations.map(([oKey, oName]) => (
                <option key={oKey} value={oKey}>{oName}</option>
              ))}
            </select>
          </div>
        )}

        {/* Department Filter */}
        {departments.length > 0 && (
          <div style={{ flex: '1 1 120px', minWidth: 110 }}>
            <select
              value={selectedDept}
              onChange={e => setSelectedDept(e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '6px 6px',
                fontSize: 12,
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                background: '#fff'
              }}
            >
              <option value="">All Departments</option>
              {departments.map(([dKey, dName]) => (
                <option key={dKey} value={dKey}>{dName}</option>
              ))}
            </select>
          </div>
        )}

        {/* Sub-Department Filter */}
        {subDepartments.length > 0 && (
          <div style={{ flex: '1 1 120px', minWidth: 110 }}>
            <select
              value={selectedSubDept}
              onChange={e => setSelectedSubDept(e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '6px 6px',
                fontSize: 12,
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                background: '#fff'
              }}
            >
              <option value="">All Sub-Departments</option>
              {subDepartments.map(([sdKey, sdName]) => (
                <option key={sdKey} value={sdKey}>{sdName}</option>
              ))}
            </select>
          </div>
        )}

        {/* Rank Filter */}
        {ranks.length > 0 && (
          <div style={{ flex: '1 1 110px', minWidth: 100 }}>
            <select
              value={selectedRank}
              onChange={e => setSelectedRank(e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '6px 6px',
                fontSize: 12,
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                background: '#fff'
              }}
            >
              <option value="">All Ranks</option>
              {ranks.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        )}

        {/* Status Filter Buttons (if personnelStructures provided) */}
        {personnelStructures.length > 0 && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setAssignmentFilter('all')}
              style={{
                padding: '4px 8px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                background: assignmentFilter === 'all' ? '#1b4332' : '#e2e8f0',
                color: assignmentFilter === 'all' ? '#fff' : '#475569'
              }}
            >
              All ({personnelList.length})
            </button>
            <button
              type="button"
              onClick={() => setAssignmentFilter('unassigned')}
              style={{
                padding: '4px 8px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                background: assignmentFilter === 'unassigned' ? '#0284c7' : '#e2e8f0',
                color: assignmentFilter === 'unassigned' ? '#fff' : '#475569'
              }}
            >
              ➕ Unassigned ({personnelList.length - activeStructureMap.size})
            </button>
            <button
              type="button"
              onClick={() => setAssignmentFilter('assigned')}
              style={{
                padding: '4px 8px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                background: assignmentFilter === 'assigned' ? '#15803d' : '#e2e8f0',
                color: assignmentFilter === 'assigned' ? '#fff' : '#475569'
              }}
            >
              ✓ Assigned ({activeStructureMap.size})
            </button>
          </div>
        )}

        {/* Reset Filters Button */}
        {(activeFilterCount > 0 || assignmentFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { clearFilters(); setAssignmentFilter('all'); }}
            title="Reset all filters"
            style={{
              padding: '5px 10px',
              fontSize: 11,
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 700
            }}
          >
            ✕ Reset
          </button>
        )}
      </div>

      {/* Main Personnel Select Dropdown */}
      <select
        value={value || ''}
        onChange={handleSelect}
        required={required}
        disabled={disabled || filteredList.length === 0}
        style={{
          width: '100%',
          padding: '9px 10px',
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          fontSize: 13,
          background: '#fff',
          fontWeight: value ? 600 : 400
        }}
      >
        <option value="">
          {filteredList.length === 0
            ? '-- No personnel match your search filters --'
            : placeholder}
        </option>
        {filteredList.map(p => {
          const activeSt = activeStructureMap.get(String(p.id));
          const isAssigned = !!activeSt;
          const statusBadge = isAssigned
            ? `✓ [ASSIGNED${activeSt.salary_grade?.rank ? `: ${activeSt.salary_grade.rank}` : ''}] `
            : `➕ [UNASSIGNED] `;

          const name = p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
          const armyId = p.army_id ? `[${p.army_id}] ` : '';
          const rank = p.rank ? ` (${p.rank})` : '';
          const dept = toSomaliDept(p.department?.name || p.department_name);
          const subDept = toSomaliDept(p.sub_department?.name || p.sub_department_name);
          const phone = p.phone || p.phone_number;
          const org = p.organization || p.army_type?.name;

          const deptStr = dept ? ` | Dept: ${dept}${subDept ? ` (${subDept})` : ''}` : '';
          const orgStr = org ? ` | Org: ${org}` : '';
          const phoneStr = phone ? ` | 📞 ${phone}` : '';

          return (
            <option
              key={p.id}
              value={p.id}
              style={{
                fontWeight: isAssigned ? 700 : 400,
                color: isAssigned ? '#15803d' : '#1e293b',
                background: isAssigned ? '#f0fdf4' : '#ffffff'
              }}
            >
              {statusBadge}{armyId}{name}{rank}{deptStr}{orgStr}{phoneStr}
            </option>
          );
        })}
      </select>
    </div>
  );
};

export default PersonnelSelect;
